'use client';

import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { AnimatePresence, motion } from 'framer-motion';
import _ from 'lodash';
import { Minus, MoveLeft, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { ChatData } from '@/slw/entities/chat';
import { useChatStore } from '@/slw/shared/lib/firebase/chat-store';
import { db } from '@/slw/shared/lib/firebase/config';
import { markMessageAsSeen } from '@/slw/shared/lib/firebase/utils';
import { cn } from '@/slw/shared/lib/utils';
import { useAuthStore } from '@/slw/shared/stores/auth.store';
import { Button } from '@/slw/shared/ui/button';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/slw/shared/ui/resizable';
import { ChatMain, Sidebar } from '@/slw/widgets/chat';
const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    x: 20,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    x: 0,
    transition: {
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1],
      opacity: { duration: 0.25 },
      scale: { duration: 0.3 },
      y: { duration: 0.3 },
      x: { duration: 0.3 },
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
    x: 10,
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};

interface ChatDialogProps {
  isOpen: boolean;
  onClose: () => void;
  defaultLayout: number[] | undefined;
  defaultCollapsed?: boolean;
  navCollapsedSize: number;
}

export const ChatDialog = ({
  isOpen,
  onClose,
  defaultLayout = [320, 480],
  defaultCollapsed = false,
  navCollapsedSize,
}: ChatDialogProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [showChatList, setShowChatList] = useState(true);
  const [chatUsers, setChatUsers] = useState<ChatData[]>([]);
  const { chatId, changeChat, resetChat } = useChatStore();
  const { user: currentUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [actionType, setActionType] = useState<'close' | 'hidden' | null>(null);

  const handleChatSelect = async (chat: ChatData) => {
    // const userChats = chatUsers.map((item) => {
    //   // eslint-disable-next-line @typescript-eslint/no-unused-vars
    //   const { user, ...rest } = item;

    //   return rest;
    // });

    // const chatIndex = userChats.findIndex((item) => item.chatId === chat.chatId);

    // userChats[chatIndex].isSeen = true;
    // userChats[chatIndex].isNotificationRead = true;
    // userChats[chatIndex].updatedAt = Timestamp.fromDate(new Date());
    // const userChatsRef = doc(db, 'userchats', currentUser.id!);

    try {
      await markMessageAsSeen(chat.chatId, currentUser.id!);
      changeChat(chat.chatId, chat.user);

      // On small screens, hide the chat list and show the chat
      if (isSmallScreen) {
        setShowChatList(false);
      }
    } catch (err) {
      // Error handled
    }
  };

  const handleBackToList = () => {
    setShowChatList(true);
  };

  useEffect(() => {
    const checkScreenWidth = () => {
      const width = window.innerWidth;

      setIsMobile(width <= 768);
      setIsSmallScreen(width <= 640);
    };

    // Initial check
    checkScreenWidth();

    // Event listener for screen width changes
    window.addEventListener('resize', checkScreenWidth);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('resize', checkScreenWidth);
    };
  }, []);

  useEffect(() => {
    if (!currentUser.id) return;

    const unSub = onSnapshot(doc(db, 'userchats', currentUser.id!), async (res) => {
      try {
        const items: ChatData[] = res?.data()?.chats || [];

        const promises = items.map(async (item) => {
          const userDocRef = doc(db, 'users', item.receiverId);
          const userDocSnap = await getDoc(userDocRef);

          const user = userDocSnap.data() as ChatData['user'];

          return { ...item, user };
        });

        const chatData = await Promise.all(promises);

        setChatUsers(_.orderBy(chatData, 'createdAt', 'desc'));
      } catch (err) {
        // Error handled
      } finally {
        setIsLoading(false);
      }
    });

    return () => {
      unSub();
    };
  }, [currentUser.id, isOpen, actionType]);

  if (!isOpen) return null;

  const handleToggeWindowChat = () => {
    setActionType('hidden');
    onClose();
  };

  const handleClose = () => {
    setActionType('close');
    setChatUsers([]);
    resetChat();
    onClose();
  };

  return (
    <AnimatePresence mode='wait'>
      {isOpen && (
        // Portal-like behavior với createPortal hoặc fixed positioning
        <motion.div
          className='fixed bottom-0 right-0 z-50 w-full lg:w-[40rem] 
          md:w-[35rem] xl:w-[45rem] 2xl:w-[56rem] md:h-[30rem] h-[80vh]
            xl:h-[40rem] lg:h-[35rem]  bg-white rounded-lg 
            shadow-2xl border border-gray-200 overflow-hidden outline-none'
          variants={modalVariants}
          initial='hidden'
          animate='visible'
          exit='exit'
        >
          <div className='flex flex-col h-full overflow-hidden '>
            <main className='container mx-auto h-full  overflow-hidden !px-0'>
              <div className='z-10 w-full h-full text-sm flex overflow-hidden'>
                {isSmallScreen ? (
                  <div className='h-full w-full flex flex-col'>
                    {showChatList ? (
                      <div className='h-full w-full flex flex-col'>
                        <div className='p-2 border-b flex items-center justify-between'>
                          <h2 className='text-xl font-bold'>Messages</h2>
                          <div className='flex items-center space-x-1 my-auto'>
                            <Button
                              color='light'
                              size='sm'
                              className='flex items-center justify-center'
                              onClick={handleToggeWindowChat}
                            >
                              <Minus size={16} className='text-gray-600' />
                            </Button>
                            <Button
                              size='sm'
                              color='light'
                              onClick={handleClose}
                              className='flex items-center justify-center'
                            >
                              <X size={16} className='text-gray-600' />
                            </Button>
                          </div>
                        </div>
                        <div className='flex-1 overflow-hidden'>
                          <Sidebar
                            isCollapsed={false}
                            chats={chatUsers}
                            isMobile={true}
                            onChatSelect={handleChatSelect}
                            selectedChatId={chatId}
                            loading={isLoading}
                          />
                        </div>
                        <div className='flex items-center space-x-1 justify-end p-2 border-t'>
                          <Button color='light' onClick={handleClose} className=''>
                            <X size={16} className='text-gray-600' />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className='h-full w-full flex flex-col'>
                        <div className='flex items-center justify-between px-2 border-b'>
                          <Button
                            size='sm'
                            color='light'
                            onClick={handleBackToList}
                            className='flex items-center text-gray-500'
                          >
                            <MoveLeft />
                          </Button>
                          <div className='flex items-center space-x-1 my-auto'>
                            <Button
                              color='light'
                              size='sm'
                              className='flex items-center justify-center'
                              onClick={handleToggeWindowChat}
                            >
                              <Minus size={16} className='text-gray-600' />
                            </Button>
                            <Button
                              size='sm'
                              color='light'
                              onClick={handleClose}
                              className='flex items-center justify-center'
                            >
                              <X size={16} className='text-gray-600' />
                            </Button>
                          </div>
                        </div>
                        {chatId && <ChatMain isMobile={true} className='flex-1' />}
                      </div>
                    )}
                  </div>
                ) : (
                  <ResizablePanelGroup
                    direction='horizontal'
                    onLayout={(sizes: number[]) => {
                      document.cookie = `react-resizable-panels:layout=${JSON.stringify(sizes)}`;
                    }}
                    className='h-full items-stretch'
                  >
                    <ResizablePanel
                      defaultSize={isCollapsed ? navCollapsedSize : defaultLayout[0]}
                      collapsedSize={navCollapsedSize}
                      collapsible={true}
                      minSize={isMobile ? 0 : 24}
                      maxSize={isMobile ? 8 : 30}
                      onCollapse={() => {
                        setIsCollapsed(true);
                        localStorage.setItem('react-resizable-panels:collapsed', JSON.stringify(true));
                      }}
                      onExpand={() => {
                        setIsCollapsed(false);
                        localStorage.setItem('react-resizable-panels:collapsed', JSON.stringify(false));
                      }}
                      className={cn(
                        isCollapsed && 'min-w-[50px]  transition-all duration-300 ease-in-out',
                        !isCollapsed ? 'xl:p-8 lg:p-6 pb-1 md:p-4 p-2' : 'p-1',
                      )}
                    >
                      {!isCollapsed && <h2 className='text-xl font-bold mb-2.5'>Message</h2>}
                      <Sidebar
                        isCollapsed={isCollapsed || isMobile}
                        chats={chatUsers}
                        isMobile={isMobile}
                        onChatSelect={handleChatSelect}
                        selectedChatId={chatId}
                        loading={isLoading}
                      />
                    </ResizablePanel>
                    <ResizableHandle withHandle />
                    <ResizablePanel defaultSize={defaultLayout[1]} minSize={30}>
                      <div className='flex items-center space-x-1 justify-end'>
                        <Button color='light' className='h-fit' onClick={handleToggeWindowChat}>
                          <Minus size={16} className='text-gray-600' />
                        </Button>
                        <Button color='light' onClick={handleClose} className='h-fit'>
                          <X size={16} className='text-gray-600' />
                        </Button>
                      </div>
                      {chatId && <ChatMain isMobile={isMobile} className='' />}
                    </ResizablePanel>
                  </ResizablePanelGroup>
                )}
              </div>
            </main>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
